import { Modal, Button, Tabs, LoadingOverlay } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useState, useEffect, useRef } from "react";
import { IconMinus, IconWindowMaximize, IconX } from "@tabler/icons-react";
// @ts-ignore - side-effect CSS import handled by bundler
import "../../styles/notifications.css";

// Import all extracted components
import InformationTab from "./tabs/InformationTab";
import PlannedWorkStepsTab from "./tabs/PlannedWorkStepsTab";
import DiaryTab from "./tabs/DiaryTab";
import RealisedWorkStepsTab from "./tabs/RealisedWorkStepsTab";
import MeasurementsTab from "./tabs/MeasurementsTab";
import UnitPricesTab from "./tabs/UnitPricesTab";
import DeviationsTab from "./tabs/DeviationsTab";
import ExceptionReportsTab from "./tabs/ExceptionReportsTab";
import SelfDeliveryTab from "./tabs/SelfDeliveryTab";
import MvrTab from "./tabs/MvrTab";
import ContractMemoTab from "./tabs/ContractMemoTab";
import ActionHistoryTab from "./tabs/ActionHistoryTab";

// Import shared types
import { ObjectData, ObjectFormProps, WorkStep, STARA_OBJECT_FIELD_DEFAULTS } from "./types/ObjectFormTypes";
import useTransientStore from "../../data/transcientStore";
import { usePermissions } from "../../hooks/usePermissions";
import { useAddressSystemFeatures } from "../../hooks/useAddressSystemFeatures";
import { useTranslation } from "../../hooks/useTranslation";

export default function ObjectForm({
  opened,
  onClose,
  objectData,
  onSubmit,
  addressSystemId,
  userId,
  onDrawingStateChange,
}: Readonly<ObjectFormProps>) {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const currentUserId = useTransientStore((state) => state.currentUserId);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({
    pageId: 308,
    addressSystemId: addrIdFromStore || 0,
    autoLoad: true,
  });

  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>("information");
  const [workSteps, setWorkSteps] = useState<WorkStep[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const previousObjectIdRef = useRef<number | null>(null);
  const triggerClearDrawing = useTransientStore(
    (state) => state.triggerClearDrawing,
  );

  // Check permission for modifying objects
  const { checkAccess } = usePermissions();
  const canModify = checkAccess("CITY_OBJECT_MODIFY");
  const canShowPrices = checkAccess("PRICES_SHOW");
  const canShowWorkPlanned = checkAccess("WORK_PLANNED_SHOW");
  const canShowWorkImplemented = checkAccess("WORK_IMPLEMENTED_SHOW");
  const canModifyDiary = checkAccess("DIARY_MODIFY");
  const canModifyDates = checkAccess("CITY_OBJECT_DATE_MODIFY");
  const canShowAttachments = checkAccess("CITY_OBJECT_ATTACHMENT_SHOW");
  const canShowDeviations = checkAccess("CITY_OBJECT_DEVIATION_SHOW");
  const canShowExceptionReports = checkAccess(
    "CITY_OBJECT_EXCEPTION_REPORT_SHOW",
  );
  const canShowActionHistory = checkAccess("CITY_OBJECT_LOG");
  const { hasDeviations, hasExceptionReports, hasActionHistory } = useAddressSystemFeatures();
  const showDeviations = hasDeviations && canShowDeviations;
  const showExceptionReports =
    hasExceptionReports && canShowExceptionReports;
  const showActionHistory = hasActionHistory && canShowActionHistory;

  // Stara-specific feature flags (per address system)
  const { hasSelfDelivery, hasContractMemo, hasMvr } = useAddressSystemFeatures();

  // Helper function to infer drawing type from WKT
  const getDrawingTypeFromWKT = (wkt: string, fallback = ""): string => {
    if (!wkt) return fallback || "Unknown";
    const wktUpper = wkt.toUpperCase();
    if (wktUpper.includes("POINT")) return "Point";
    if (wktUpper.includes("LINESTRING") || wktUpper.includes("MULTILINESTRING"))
      return "Line";
    if (wktUpper.includes("POLYGON") || wktUpper.includes("MULTIPOLYGON"))
      return "Polygon";
    return fallback || "Unknown";
  };

  const handleGeometryUpdate = (wkt: string, drawingType: string) => {
    form.setFieldValue("wkt", wkt);
    form.setFieldValue("drawingType", drawingType);
    // Restore modal after successful geometry capture
    setIsMinimized(false);
  };

  const handleDrawingStart = () => {
    setIsMinimized(true); // Minimize the modal
    onDrawingStateChange?.(true); // Notify parent
  };

  const handleDrawingStop = () => {
    // Don't restore modal here - only restore on successful capture
    // This prevents modal from reopening when drawing is interrupted
    onDrawingStateChange?.(false); // Notify parent
  };

  const form = useForm<ObjectData>({
    initialValues: {
      id: 0,
      wkt: "",
      drawingType: "",
      job: "",
      name: "",
      streetName: "",
      orderName: "",
      networkName: "",
      status: "",
      type: "",
      typeDesc: "",
      plannedYear: null,
      measures: "",
      lanes: "",
      levelOfReadiness: "",
      subscriber: null,
      foreman: null,
      contractorForeman: null,
      projectManager: null,
      qualityControl: "",
      qualityControlComments: "",
      pvm: null,
      agreedPvm: null,
      completedPvm: null,
      approvedPvm: null,
      invoicingPvm: "",
      warrantyFollowUp: "",
      warrantyFollowUpComments: "",
      description: "",
      imageAttachment: null,
      coatingResearchReport: [],
      attachedFiles: [],
      existingFiles: [],
      existingQcFiles: [],
      fileIdsToKeep: [],
      qcFileIdsToKeep: [],
      plannedWorkSteps: [],
      userRightsType: "general",
      rolePermissions: [],
      targetContract: "",
      newObjectName: "",
      drillLocation: null,
      modifier: null,
      modified: null,
      checkedToInvoice: false,
      checkedToInvoiceDate: null,
      checkedToInvoiceUserName: null,
      signingLocked: false,
      implementedComment: "",
      measurementDate: null,
      measurementPersons: "",
      contractorWorkNumber: "",
      ...STARA_OBJECT_FIELD_DEFAULTS,
    },
    validate: {
      job: (value: string) =>
        value?.trim()
          ? null
          : getTranslation("LABEL_JOB_REQUIRED") || "Job is required",
      name: (value: string) =>
        value?.trim()
          ? null
          : getTranslation("LABEL_NAME_REQUIRED") || "Name is required",
      status: (value: string) =>
        value?.trim()
          ? null
          : getTranslation("LABEL_STATUS_REQUIRED") || "Status is required",
      type: (value: string) =>
        value?.trim()
          ? null
          : getTranslation("LABEL_TYPE_REQUIRED") || "Type is required",
    },
  });

  // Update form values when objectData prop changes
  useEffect(() => {
    if (!opened || !objectData) return;

    const isNewObject = previousObjectIdRef.current !== objectData.id;

    if (isNewObject || form.getValues().id !== objectData.id) {
      const drawingType = getDrawingTypeFromWKT(
        objectData.wkt,
        objectData.drawingType,
      );
      form.setValues({
        ...objectData,
        drawingType,
      });
      setWorkSteps(objectData.plannedWorkSteps || []);
      setShowSuccessAlert(false);
      previousObjectIdRef.current = objectData.id;
      if (isNewObject) {
        setIsMinimized(false);
        setActiveTab("information");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, objectData?.id, objectData]);

  // Sync minimized state with external open prop
  useEffect(() => {
    if (opened) {
      // When opened becomes true, ensure we show the full modal (not minimized)
      setIsMinimized(false);
      setActiveTab("information");
    } else {
      // When opened becomes false (parent cleared), force minimized state off so floating label disappears
      setIsMinimized(false);
    }
  }, [opened]);

  useEffect(() => {
    if (!showDeviations && activeTab === "deviations") {
      setActiveTab("information");
    }
  }, [activeTab, showDeviations]);

  useEffect(() => {
    if (!showExceptionReports && activeTab === "exception-reports") {
      setActiveTab("information");
    }
  }, [activeTab, showExceptionReports]);

  useEffect(() => {
    if (isUpdating) {
      const modalBody = document.querySelector(
        '[class*="mantine-Modal-body"]',
      ) as HTMLElement;
      if (modalBody) {
        modalBody.style.overflow = "hidden";
        modalBody.style.maxHeight =
          modalBody.style.maxHeight || "calc(100vh - 200px)";
      }
    } else {
      const modalBody = document.querySelector(
        '[class*="mantine-Modal-body"]',
      ) as HTMLElement;
      if (modalBody) {
        modalBody.style.overflow = "hidden";
      }
    }
  }, [isUpdating]);

  const handleSubmit = (values: ObjectData) => {
    setShowSuccessAlert(true);
    setTimeout(() => {
      setShowSuccessAlert(false);
    }, 3000);
  };

  // Handler for CLOSING the modal (X button, ESC, clicking outside)
  const handleClose = () => {
    // Synchronously stop drawing via direct store access
    const { setDrawing, map } = useTransientStore.getState();

    if (map) {
      // Direct synchronous cleanup of map interactions
      map.getInteractions().forEach((interaction) => {
        const name = interaction.getProperties()?.name;
        if (
          name &&
          (name.includes("Point") ||
            name.includes("Polygon") ||
            name.includes("LineString"))
        ) {
          interaction.setActive(false);
        }
      });
    }

    // Update state
    setDrawing(false);
    triggerClearDrawing();
    onDrawingStateChange?.(false);

    // Now safe to close immediately - interactions already stopped
    form.reset();
    setShowSuccessAlert(false);
    setIsMinimized(false);
    setActiveTab("information");
    setWorkSteps([]);
    previousObjectIdRef.current = null;
    onClose();
  };

  // Handler for CANCEL button in view/edit mode - resets to original values
  const handleCancel = () => {
    if (objectData) {
      const drawingType = getDrawingTypeFromWKT(
        objectData.wkt,
        objectData.drawingType,
      );
      form.setValues({
        ...objectData,
        drawingType,
      });
      setWorkSteps(objectData.plannedWorkSteps || []);
    }
    setShowSuccessAlert(false);
    onDrawingStateChange?.(false); // Stop drawing if active
    triggerClearDrawing(); // Clear any newly drawn geometry from map
    setActiveTab("information"); // Return to information tab
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    useTransientStore.getState().setModalMinimized(true);
  };

  const handleRestore = () => {
    setIsMinimized(false);
    useTransientStore.getState().setModalMinimized(false);
  };

  if (!objectData || !opened) return null; // Do not render minimized label when parent says closed

  const minimizedButton = isMinimized ? (
    <button
      type="button"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleRestore();
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
        handleRestore();
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateX(-50%) translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.22)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateX(-50%)";
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 0, 0, 0.15)";
      }}
      style={{
        position: "fixed",
        bottom: "170px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
        backgroundColor: "#ffffff",
        border: "1px solid #228be6",
        borderRadius: "12px",
        padding: "8px 14px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        alignItems: "center",
        backdropFilter: "blur(8px)",
        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          paddingLeft: "6px",
        }}
      >
        <IconWindowMaximize size={16} color="#228be6" strokeWidth={2} />
        <span
          style={{
            fontWeight: 600,
            fontSize: "13px",
            color: "#228be6",
            letterSpacing: "0.2px",
          }}
        >
          {objectData.name ||
            getTranslation("LABEL_SELECTED_OBJECT") ||
            "Selected Object"}
        </span>
        <Button
          variant="subtle"
          color="gray"
          size="xs"
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          style={{
            marginLeft: "2px",
            padding: "6px",
            border: "none",
            minWidth: "auto",
            height: "auto",
          }}
        >
          <IconX size={14} />
        </Button>
      </div>
    </button>
  ) : null;

  return (
    <>
      {minimizedButton}
      <Modal
        opened={opened}
        onClose={isUpdating ? () => {} : handleClose}
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              position: "relative",
            }}
          >
            <span
              style={{
                width: "100%",
                textAlign: "center",
                paddingRight: "60px",
              }}
            >{`${getTranslation("TITLE_OBJECT_INSPECTION") || "Object Inspection"} - ${
              objectData.name ||
              getTranslation("LABEL_NEW_OBJECT") ||
              "New Object"
            }`}</span>
            <Button
              variant="subtle"
              size="xs"
              onClick={handleMinimize}
              title={getTranslation("TITLE_MINIMIZE") || "Minimize"}
              style={{
                padding: "4px 8px",
                marginRight: "8px",
                position: "absolute",
                right: "0",
              }}
            >
              <IconMinus size={16} />
            </Button>
          </div>
        }
        size="90%"
        radius="md"
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={!isUpdating}
        lockScroll={true}
        style={{ maxWidth: "none", display: isMinimized ? "none" : "block" }}
        styles={{
          body: {
            position: "relative",
            minHeight: "400px",
            maxHeight: "calc(100vh - 330px)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
          content: { position: "relative", overflow: "hidden" },
          inner: { pointerEvents: isUpdating ? "none" : "auto" },
          header: {
            paddingRight: "0px",
            background: "linear-gradient(to bottom, #f0f0f0 0%, #e5e5e5 100%)",
            borderBottom: "1px solid #d0d0d0",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.15)",
            minHeight: "40px",
            padding: "8px 16px",
          },
          close: { marginLeft: "0px", marginRight: "12px" },
          title: {
            width: "100%",
            flex: "1",
            fontWeight: "bold",
            color: "#1976d2",
          },
        }}
      >
        <div
          style={{
            position: "relative",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            pointerEvents: isUpdating ? "none" : "auto",
          }}
        >
          {isUpdating && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.5)",
                backdropFilter: "blur(3px)",
                zIndex: 1001,
                pointerEvents: "all",
              }}
            >
              <LoadingOverlay
                visible={true}
                loaderProps={{ size: "lg" }}
                styles={{
                  root: {
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    backgroundColor: "transparent",
                  },
                }}
              />
            </div>
          )}
          <Tabs
            value={activeTab}
            onChange={setActiveTab}
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflow: "hidden",
            }}
          >
            <Tabs.List
              grow
              style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: "white",
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <Tabs.Tab value="information">
                {getTranslation("TAB_INFORMATION") || "Information"}
              </Tabs.Tab>
              {!canShowWorkPlanned && (
                <Tabs.Tab value="planned-work" disabled={true}>
                  {getTranslation("TAB_PLANNED_WORK_STEPS") ||
                    "Planned Work Steps"}
                </Tabs.Tab>
              )}
              {canShowWorkPlanned && (
                <Tabs.Tab value="planned-work">
                  {getTranslation("TAB_PLANNED_WORK_STEPS") ||
                    "Planned Work Steps"}
                </Tabs.Tab>
              )}
              <Tabs.Tab value="diary">
                {getTranslation("TAB_DIARY") || "Diary"}
              </Tabs.Tab>
              {!canShowWorkImplemented && (
                <Tabs.Tab value="realised-work" disabled={true}>
                  {getTranslation("TAB_REALISED_WORK_STEPS") ||
                    "Realised Work Steps"}
                </Tabs.Tab>
              )}
              {canShowWorkImplemented && (
                <Tabs.Tab value="realised-work">
                  {getTranslation("TAB_REALISED_WORK_STEPS") ||
                    "Realised Work Steps"}
                </Tabs.Tab>
              )}
              {!canShowAttachments && (
                <Tabs.Tab value="measurements" disabled={true}>
                  {getTranslation("TAB_MEASUREMENT_PROTOCOL_APPENDICES") ||
                    "Measurement Protocol Appendices"}
                </Tabs.Tab>
              )}
              {canShowAttachments && (
                <Tabs.Tab value="measurements">
                  {getTranslation("TAB_MEASUREMENT_PROTOCOL_APPENDICES") ||
                    "Measurement Protocol Appendices"}
                </Tabs.Tab>
              )}
              {!canShowPrices && (
                <Tabs.Tab value="unit-prices" disabled={true}>
                  {getTranslation("TAB_UNIT_PRICES") || "Unit Prices"}
                </Tabs.Tab>
              )}
              {canShowPrices && (
                <Tabs.Tab value="unit-prices">
                  {getTranslation("TAB_UNIT_PRICES") || "Unit Prices"}
                </Tabs.Tab>
              )}
              {hasContractMemo && (
                <Tabs.Tab value="contract-memo">
                  {getTranslation("TAB_CONTRACT_MEMO") || "Contract Memo"}
                </Tabs.Tab>
              )}
              {showDeviations && (
                <Tabs.Tab value="deviations">
                  {getTranslation("TAB_DEVIATIONS") || "Deviations"}
                </Tabs.Tab>
              )}
              {showExceptionReports && (
                <Tabs.Tab value="exception-reports">
                  {getTranslation("TAB_EXCEPTION_REPORTS") ||
                    "Exception Reports"}
                </Tabs.Tab>
              )}
              {hasSelfDelivery && (
                <Tabs.Tab value="self-delivery">
                  {getTranslation("TAB_SELF_DELIVERY") || "Self Delivery"}
                </Tabs.Tab>
              )}
              {hasMvr && (
                <Tabs.Tab value="mvr">
                  {getTranslation("TAB_MVR") || "MVR meter"}
                </Tabs.Tab>
              )}
              {hasActionHistory && (
                <Tabs.Tab value="action-history" disabled={!canShowActionHistory}>
                  {getTranslation("CITY_OBJECT_ACTION_HISTORY_TITLE") ||
                    "Action History"}
                </Tabs.Tab>
              )}
            </Tabs.List>

            <Tabs.Panel
              value="information"
              p="md"
              style={{ flex: 1, overflow: "auto" }}
            >
              {/* {showSuccessAlert && (
            <Alert
              icon={<IconCheck size={16} />}
              title={getTranslation("TITLE_SUCCESS") || "Success!"}
              color="green"
              variant="light"
              mb="md"
            >
              Object information has been updated successfully.
            </Alert>
          )} */}
              <InformationTab
                form={form}
                showSuccessAlert={showSuccessAlert}
                handleSubmit={handleSubmit}
                handleClose={handleCancel}
                mode="view"
                addressSystemId={addressSystemId}
                userId={userId}
                onGeometryUpdate={handleGeometryUpdate}
                onDrawingStart={handleDrawingStart}
                onDrawingStop={handleDrawingStop}
                initialWkt={objectData?.wkt}
                canModify={canModify}
                isUpdating={isUpdating}
                setIsUpdating={setIsUpdating}
              />
            </Tabs.Panel>

            {canShowWorkPlanned && (
              <Tabs.Panel
                value="planned-work"
                p="md"
                style={{ flex: 1, overflow: "auto" }}
              >
                <PlannedWorkStepsTab
                  workSteps={workSteps}
                  setWorkSteps={setWorkSteps}
                  onSubmit={handleSubmit}
                  form={form}
                  canModify={canModify}
                />
              </Tabs.Panel>
            )}
            <Tabs.Panel
              value="diary"
              p="md"
              style={{ flex: 1, overflow: "auto" }}
            >
              <DiaryTab
                form={form}
                addressSystemId={addressSystemId}
                userId={userId}
                canModify={canModify}
                canModifyDiary={canModifyDiary}
                canModifyDates={canModifyDates}
              />
            </Tabs.Panel>

            {canShowWorkImplemented && (
              <Tabs.Panel
                value="realised-work"
                p="md"
                style={{ flex: 1, overflow: "auto" }}
              >
                <RealisedWorkStepsTab form={form} canModify={canModify} />
              </Tabs.Panel>
            )}

            {canShowAttachments && (
              <Tabs.Panel
                value="measurements"
                p="md"
                style={{ flex: 1, overflow: "auto" }}
              >
                <MeasurementsTab form={form} canModify={canModify} />
              </Tabs.Panel>
            )}

            {canShowPrices && (
              <Tabs.Panel
                value="unit-prices"
                p="md"
                style={{ flex: 1, overflow: "auto" }}
              >
                <UnitPricesTab form={form} canModify={canModify} />
              </Tabs.Panel>
            )}

            {hasContractMemo && (
              <Tabs.Panel
                value="contract-memo"
                p="md"
                style={{ flex: 1, overflow: "auto" }}
              >
                <ContractMemoTab form={form} canModify={canModify} />
              </Tabs.Panel>
            )}
            {showDeviations && activeTab === "deviations" && (
              <Tabs.Panel
                value="deviations"
                p="md"
                style={{ flex: 1, overflow: "auto" }}
              >
                <DeviationsTab
                  form={form}
                  addressSystemId={addressSystemId}
                  userId={userId}
                />
              </Tabs.Panel>
            )}
            {showExceptionReports && activeTab === "exception-reports" && (
              <Tabs.Panel
                value="exception-reports"
                p="md"
                style={{ flex: 1, overflow: "auto" }}
              >
                <ExceptionReportsTab
                  form={form}
                  addressSystemId={addressSystemId || addrIdFromStore}
                  userId={userId || currentUserId}
                />
              </Tabs.Panel>
            )}

            {hasSelfDelivery && (
              <Tabs.Panel
                value="self-delivery"
                p="md"
                style={{ flex: 1, overflow: "auto" }}
              >
                <SelfDeliveryTab
                  form={form}
                  addressSystemId={addressSystemId}
                  userId={userId}
                  canModify={canModify}
                />
              </Tabs.Panel>
            )}
            {hasMvr && activeTab === "mvr" && (
              <Tabs.Panel
                value="mvr"
                p="md"
                style={{ flex: 1, overflow: "auto" }}
              >
                <MvrTab
                  form={form}
                  addressSystemId={addressSystemId || addrIdFromStore}
                  userId={userId || currentUserId}
                />
              </Tabs.Panel>
            )}

            {showActionHistory && activeTab === "action-history" && (
              <Tabs.Panel
                value="action-history"
                p="md"
                style={{ flex: 1, overflow: "auto" }}
              >
                <ActionHistoryTab
                  form={form}
                  addressSystemId={addressSystemId || addrIdFromStore}
                  userId={userId || currentUserId}
                />
              </Tabs.Panel>
            )}
          </Tabs>
        </div>
      </Modal>
    </>
  );
}
